import { useState, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  Clock,
  DollarSign,
  Users,
  Play,
  Square,
  CheckCircle,
  XCircle,
  Send,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface TimesheetEntry {
  id: string
  userId: string
  clientId: string | null
  clientName: string | null
  ticketId: string | null
  description: string
  category: string | null
  date: string
  startTime: string | null
  endTime: string | null
  durationMins: number
  billable: boolean
  billableRate: number | null
  status: string
  approvedBy: string | null
  approvedAt: string | null
  notes: string | null
  createdAt: string
}

interface TimesheetStats {
  totalHoursThisWeek: number
  totalHoursThisMonth: number
  billableRatio: number
  byClient: Array<{ clientName: string; hours: number }>
  byCategory: Array<{ category: string; hours: number }>
  pendingApproval: number
}

interface WeeklySummaryDay {
  date: string
  totalMins: number
  billableMins: number
  entries: number
}

// ============================================
// Helpers
// ============================================

const categoryColors: Record<string, string> = {
  project: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  support: 'bg-green-500/10 text-green-500 border-green-500/20',
  internal: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
  travel: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  training: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
}

const categoryLabels: Record<string, string> = {
  project: 'Project',
  support: 'Support',
  internal: 'Internal',
  travel: 'Travel',
  training: 'Training',
}

function categoryBadge(category: string | null) {
  if (!category) return <Badge variant="secondary">Uncategorised</Badge>
  const color = categoryColors[category] || ''
  return <Badge className={color}>{categoryLabels[category] || category}</Badge>
}

function statusBadge(status: string) {
  switch (status) {
    case 'draft':
      return <Badge variant="secondary">Draft</Badge>
    case 'submitted':
      return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Submitted</Badge>
    case 'approved':
      return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Approved</Badge>
    case 'rejected':
      return <Badge variant="destructive">Rejected</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

function formatDuration(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

function getWeekDates(offset: number): { start: string; end: string; dates: string[] } {
  const now = new Date()
  const dayOfWeek = (now.getDay() + 6) % 7 // Monday = 0
  const monday = new Date(now)
  monday.setDate(now.getDate() - dayOfWeek + offset * 7)
  monday.setHours(0, 0, 0, 0)

  const dates: string[] = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    dates.push(d.toISOString().split('T')[0]!)
  }

  return {
    start: dates[0]!,
    end: dates[6]!,
    dates,
  }
}

// ============================================
// Create Entry Dialog
// ============================================

function CreateEntryDialog({ defaultDate }: { defaultDate?: string }) {
  const [open, setOpen] = useState(false)
  const [description, setDescription] = useState('')
  const [clientName, setClientName] = useState('')
  const [category, setCategory] = useState('')
  const [date, setDate] = useState(defaultDate || new Date().toISOString().split('T')[0]!)
  const [hours, setHours] = useState('')
  const [minutes, setMinutes] = useState('')
  const [billable, setBillable] = useState(true)
  const [notes, setNotes] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/timesheets', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] })
      toast({ title: 'Entry created', description: 'Timesheet entry has been added' })
      setOpen(false)
      resetForm()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create entry', variant: 'destructive' }),
  })

  function resetForm() {
    setDescription('')
    setClientName('')
    setCategory('')
    setDate(defaultDate || new Date().toISOString().split('T')[0]!)
    setHours('')
    setMinutes('')
    setBillable(true)
    setNotes('')
  }

  const durationMins = (parseInt(hours || '0') * 60) + parseInt(minutes || '0')

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="mr-2 h-4 w-4" /> Add Entry</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Time Entry</DialogTitle>
          <DialogDescription>Log time for a task or project.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-2">
            <Label>Description *</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What did you work on?" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client</Label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Client name" />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="travel">Travel</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Hours</Label>
              <Input type="number" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="0" min="0" max="24" />
            </div>
            <div className="space-y-2">
              <Label>Minutes</Label>
              <Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="0" min="0" max="59" step="15" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} className="rounded" />
              Billable
            </label>
            {durationMins > 0 && (
              <span className="text-sm text-muted-foreground">Total: {formatDuration(durationMins)}</span>
            )}
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes..." rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({
              description,
              clientName: clientName || undefined,
              category: category || undefined,
              date,
              durationMins,
              billable,
              notes: notes || undefined,
            })}
            disabled={!description || !date || durationMins <= 0 || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Add Entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Timer Component
// ============================================

function TimerWidget() {
  const [running, setRunning] = useState(false)
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [description, setDescription] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined
    if (running && startedAt) {
      interval = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAt) / 1000))
      }, 1000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [running, startedAt])

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/timesheets', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] })
      toast({ title: 'Time saved', description: 'Timer entry has been logged' })
      setDescription('')
      setElapsed(0)
    },
    onError: () => toast({ title: 'Error', description: 'Failed to save time entry', variant: 'destructive' }),
  })

  const handleStart = useCallback(() => {
    setStartedAt(Date.now())
    setRunning(true)
    setElapsed(0)
  }, [])

  const handleStop = useCallback(() => {
    setRunning(false)
    const durationMins = Math.max(1, Math.round(elapsed / 60))

    if (description) {
      saveMutation.mutate({
        description,
        date: new Date().toISOString().split('T')[0],
        durationMins,
        billable: true,
      })
    }
  }, [elapsed, description, saveMutation])

  const formatTimer = (seconds: number) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <Input
            placeholder="What are you working on?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="flex-1"
          />
          <span className="font-mono text-lg font-bold min-w-[100px] text-center">
            {formatTimer(elapsed)}
          </span>
          {!running ? (
            <Button size="icon" onClick={handleStart} disabled={!description}>
              <Play className="h-4 w-4" />
            </Button>
          ) : (
            <Button size="icon" variant="destructive" onClick={handleStop}>
              <Square className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================
// Approval Queue
// ============================================

function ApprovalQueue() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: pendingData, isLoading } = useQuery({
    queryKey: ['timesheets', 'pending'],
    queryFn: () => apiClient.get<{ data: TimesheetEntry[] }>('/timesheets', { status: 'submitted', limit: 50 }),
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => apiClient.patch(`/timesheets/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] })
      toast({ title: 'Approved', description: 'Entry has been approved' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to approve entry', variant: 'destructive' }),
  })

  const rejectMutation = useMutation({
    mutationFn: (id: string) => apiClient.patch(`/timesheets/${id}/reject`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] })
      toast({ title: 'Rejected', description: 'Entry has been rejected' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to reject entry', variant: 'destructive' }),
  })

  const entries = pendingData?.data?.data || pendingData?.data || []
  const pending = Array.isArray(entries) ? entries : []

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (pending.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <CheckCircle className="h-12 w-12 mb-4 opacity-50 text-green-500" />
          <p className="text-lg font-medium text-muted-foreground">All caught up</p>
          <p className="text-sm text-muted-foreground">No entries pending approval</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Category</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pending.map((entry: TimesheetEntry) => (
            <TableRow key={entry.id}>
              <TableCell className="text-sm">{formatDate(entry.date)}</TableCell>
              <TableCell className="font-medium">{entry.description}</TableCell>
              <TableCell className="text-sm">{entry.clientName || '-'}</TableCell>
              <TableCell className="font-mono text-sm">{formatDuration(entry.durationMins)}</TableCell>
              <TableCell>{categoryBadge(entry.category)}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-green-500 hover:text-green-600"
                    onClick={() => approveMutation.mutate(entry.id)}
                    disabled={approveMutation.isPending}
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-red-500 hover:text-red-600"
                    onClick={() => rejectMutation.mutate(entry.id)}
                    disabled={rejectMutation.isPending}
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}

// ============================================
// Weekly View
// ============================================

function WeeklyView() {
  const [weekOffset, setWeekOffset] = useState(0)
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const week = getWeekDates(weekOffset)

  const { data: summaryData } = useQuery({
    queryKey: ['timesheets', 'summary', week.start, week.end],
    queryFn: () => apiClient.get<WeeklySummaryDay[]>('/timesheets/summary', {
      dateFrom: week.start,
      dateTo: week.end,
    }),
  })

  const { data: entriesData, isLoading } = useQuery({
    queryKey: ['timesheets', 'week', week.start, week.end],
    queryFn: () => apiClient.get<{ data: TimesheetEntry[] }>('/timesheets', {
      dateFrom: week.start,
      dateTo: week.end,
      limit: 100,
      sort: 'date',
      order: 'asc',
    }),
  })

  const submitMutation = useMutation({
    mutationFn: (id: string) => apiClient.patch(`/timesheets/${id}/submit`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] })
      toast({ title: 'Submitted', description: 'Entry submitted for approval' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to submit entry', variant: 'destructive' }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/timesheets/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] })
      toast({ title: 'Deleted', description: 'Entry has been removed' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete entry', variant: 'destructive' }),
  })

  const summary = summaryData?.data || []
  const summaryDays = Array.isArray(summary) ? summary : []
  const rawEntries = entriesData?.data?.data || entriesData?.data || []
  const entries = Array.isArray(rawEntries) ? rawEntries : []

  // Group entries by date
  const entriesByDate: Record<string, TimesheetEntry[]> = {}
  for (const date of week.dates) {
    entriesByDate[date] = []
  }
  for (const entry of entries) {
    const dateKey = entry.date.split('T')[0]!
    if (entriesByDate[dateKey]) {
      entriesByDate[dateKey].push(entry)
    }
  }

  const weekLabel = `${formatDate(week.start)} - ${formatDate(week.end)}`

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeekOffset(weekOffset - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[200px] text-center">{weekLabel}</span>
          <Button variant="outline" size="icon" onClick={() => setWeekOffset(weekOffset + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {weekOffset !== 0 && (
            <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)}>Today</Button>
          )}
        </div>
      </div>

      {/* Day Summary Bars */}
      <div className="grid grid-cols-7 gap-2">
        {week.dates.map((date) => {
          const day = summaryDays.find((s: WeeklySummaryDay) => s.date === date)
          const totalHours = day ? day.totalMins / 60 : 0
          const barHeight = Math.min(100, (totalHours / 8) * 100)
          const isToday = date === new Date().toISOString().split('T')[0]

          return (
            <div key={date} className={`text-center space-y-1 rounded-lg p-2 ${isToday ? 'bg-primary/5 border border-primary/20' : ''}`}>
              <p className="text-xs text-muted-foreground">
                {new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short' })}
              </p>
              <p className="text-xs font-medium">
                {new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
              </p>
              <div className="h-16 flex items-end justify-center">
                <div
                  className="w-6 rounded-t bg-primary/60 transition-all"
                  style={{ height: `${barHeight}%`, minHeight: totalHours > 0 ? '4px' : '0' }}
                />
              </div>
              <p className="text-xs font-mono font-bold">{totalHours > 0 ? `${totalHours.toFixed(1)}h` : '-'}</p>
            </div>
          )
        })}
      </div>

      {/* Daily Entries */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {week.dates.map((date) => {
            const dayEntries = entriesByDate[date] || []
            const dayTotal = dayEntries.reduce((sum, e) => sum + e.durationMins, 0)

            return (
              <Card key={date}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">
                      {formatDate(date)}
                      {dayTotal > 0 && (
                        <span className="ml-2 font-mono text-muted-foreground">({formatDuration(dayTotal)})</span>
                      )}
                    </CardTitle>
                    <CreateEntryDialog defaultDate={date} />
                  </div>
                </CardHeader>
                {dayEntries.length > 0 && (
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {dayEntries.map((entry: TimesheetEntry) => (
                        <div key={entry.id} className="flex items-center gap-3 py-2 border-b last:border-0">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{entry.description}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {entry.clientName && (
                                <span className="text-xs text-muted-foreground">{entry.clientName}</span>
                              )}
                              {categoryBadge(entry.category)}
                              {entry.billable && (
                                <Badge variant="outline" className="text-xs">
                                  <DollarSign className="h-3 w-3 mr-0.5" />Billable
                                </Badge>
                              )}
                            </div>
                          </div>
                          <span className="font-mono text-sm font-bold">{formatDuration(entry.durationMins)}</span>
                          <div className="flex items-center gap-1">
                            {statusBadge(entry.status)}
                            {entry.status === 'draft' && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                title="Submit for approval"
                                onClick={() => submitMutation.mutate(entry.id)}
                              >
                                <Send className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {(entry.status === 'draft' || entry.status === 'rejected') && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-destructive"
                                title="Delete entry"
                                onClick={() => deleteMutation.mutate(entry.id)}
                              >
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ============================================
// Main Page
// ============================================

export function TimesheetsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['timesheets', 'stats'],
    queryFn: () => apiClient.get<TimesheetStats>('/timesheets/stats'),
  })

  // Fetch all entries for list view
  const { data: allEntriesData, isLoading: allLoading } = useQuery({
    queryKey: ['timesheets', 'all', statusFilter, categoryFilter],
    queryFn: () => apiClient.get<{ data: TimesheetEntry[] }>('/timesheets', {
      ...(statusFilter !== 'all' && { status: statusFilter }),
      ...(categoryFilter !== 'all' && { category: categoryFilter }),
      limit: 100,
    }),
  })

  const stats = statsData?.data || null
  const rawAllEntries = allEntriesData?.data?.data || allEntriesData?.data || []
  const allEntries = Array.isArray(rawAllEntries) ? rawAllEntries : []

  const filtered = allEntries.filter((e: TimesheetEntry) =>
    e.description.toLowerCase().includes(search.toLowerCase()) ||
    (e.clientName || '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Timesheets</h1>
          <p className="text-muted-foreground">Track time, manage approvals, and view reports</p>
        </div>
        <CreateEntryDialog />
      </div>

      {/* Timer */}
      <TimerWidget />

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Hours This Week</p>
            </div>
            <div className="text-2xl font-bold mt-1">{stats?.totalHoursThisWeek?.toFixed(1) ?? '0.0'}</div>
            <p className="text-xs text-muted-foreground mt-1">of 40h target</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-500" />
              <p className="text-xs text-muted-foreground">Billable Ratio</p>
            </div>
            <div className="text-2xl font-bold text-green-500 mt-1">{stats?.billableRatio ?? 0}%</div>
            <p className="text-xs text-muted-foreground mt-1">This month</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" />
              <p className="text-xs text-muted-foreground">Top Client</p>
            </div>
            <div className="text-lg font-bold text-blue-500 mt-1 truncate">
              {stats?.byClient?.[0]?.clientName || 'None'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.byClient?.[0]?.hours ? `${stats.byClient[0].hours}h this month` : 'No entries'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Send className="h-4 w-4 text-yellow-500" />
              <p className="text-xs text-muted-foreground">Pending Approval</p>
            </div>
            <div className="text-2xl font-bold text-yellow-500 mt-1">{stats?.pendingApproval ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">entries awaiting review</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="weekly">
        <TabsList>
          <TabsTrigger value="weekly">Weekly View</TabsTrigger>
          <TabsTrigger value="list">All Entries</TabsTrigger>
          <TabsTrigger value="approvals">Approvals</TabsTrigger>
        </TabsList>

        <TabsContent value="weekly" className="space-y-4">
          <WeeklyView />
        </TabsContent>

        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search entries..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="project">Project</SelectItem>
                <SelectItem value="support">Support</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="travel">Travel</SelectItem>
                <SelectItem value="training">Training</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {allLoading ? (
            <Card>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex gap-4">
                      <Skeleton className="h-5 w-24" />
                      <Skeleton className="h-5 w-40" />
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-5 w-20" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Clock className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">No entries found</p>
                <p className="text-sm text-muted-foreground">Start tracking time to see entries here</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Billable</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((entry: TimesheetEntry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm">{formatDate(entry.date)}</TableCell>
                      <TableCell className="font-medium max-w-[300px] truncate">{entry.description}</TableCell>
                      <TableCell className="text-sm">{entry.clientName || '-'}</TableCell>
                      <TableCell>{categoryBadge(entry.category)}</TableCell>
                      <TableCell className="font-mono text-sm">{formatDuration(entry.durationMins)}</TableCell>
                      <TableCell>
                        {entry.billable ? (
                          <Badge variant="outline" className="text-xs"><DollarSign className="h-3 w-3 mr-0.5" />Yes</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">No</span>
                        )}
                      </TableCell>
                      <TableCell>{statusBadge(entry.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="approvals">
          <ApprovalQueue />
        </TabsContent>
      </Tabs>
    </div>
  )
}
